/**
 * Rythme d'encaissement des cotisations : où en sommes-nous, à cette date ?
 *
 * Un cumul de sept mois ne se compare pas à un total annuel. La question — « la
 * projection est-elle probable » — demande de confronter l'exercice en cours à
 * ce que faisaient les exercices précédents au même moment.
 *
 * **Ce que les sources permettent, et ce qu'elles ne permettent pas.** Les
 * dates de règlement ne sont renseignées dans Directus que depuis 2026 : 1 à
 * 3 % des cotisations acquittées des exercices clos en portent une. Aucune
 * saisonnalité historique n'est donc établissable, et ce module ne prétend pas
 * en établir une. La courbe mensuelle n'est produite que pour les exercices
 * suffisamment datés, et leur couverture est affichée.
 *
 * Le jugement s'appuie donc sur ce qui est fiable :
 *   — l'**assiette d'appel**, c'est-à-dire les lignes de cotisation ouvertes
 *     sur l'exercice, connue à tout moment ;
 *   — le **taux de recouvrement** des exercices clos, solide sur huit ans ;
 *   — le **produit moyen** constaté ;
 *   — le nombre de **factures émises**, comparé à la même date de l'exercice
 *     précédent, que la table de facturation porte exercice par exercice.
 */
import { cleCotisant, estPayeur, sommeExacte } from './adherents.js';
/**
 * Couverture minimale de dates de règlement pour qu'une courbe mensuelle soit
 * jugée exploitable. En deçà, la courbe décrit une poignée de lignes et non
 * l'exercice.
 */
export const COUVERTURE_MINIMALE_DATES = 0.6;
/**
 * Courbe cumulée de chaque exercice demandé, et couverture de ses dates.
 *
 * Le total annuel est le total **réel** — toutes les cotisations acquittées —
 * tandis que la courbe ne porte que les règlements datés. Rapporter l'un à
 * l'autre donne la couverture, et rend visible ce que la courbe ne dit pas.
 */
export function rythmeCotisations(cotisations, exercices, moisArrete, exerciceEnCours) {
    return exercices.map((annee) => {
        const payeuses = cotisations.filter((c) => c.annee === annee).filter(estPayeur);
        const totalMontant = sommeExacte(payeuses.map((l) => l.montant));
        const vuesTotal = new Set();
        payeuses.forEach((l, rang) => vuesTotal.add(cleCotisant(l, rang)));
        const vues = new Set();
        const parMois = new Map();
        let montantDate = 0;
        payeuses.forEach((l, rang) => {
            const mois = l.datePaiement !== null ? Number(l.datePaiement.slice(5, 7)) : Number.NaN;
            if (!Number.isInteger(mois) || mois < 1 || mois > 12)
                return;
            const cur = parMois.get(mois) ?? { payeurs: 0, montant: 0 };
            const cle = cleCotisant(l, rang);
            if (!vues.has(cle)) {
                vues.add(cle);
                cur.payeurs += 1;
            }
            cur.montant = sommeExacte([cur.montant, l.montant]);
            montantDate = sommeExacte([montantDate, l.montant]);
            parMois.set(mois, cur);
        });
        let payeurs = 0;
        let montant = 0;
        const points = [];
        for (let m = 1; m <= 12; m++) {
            const c = parMois.get(m);
            payeurs += c?.payeurs ?? 0;
            montant = sommeExacte([montant, c?.montant ?? 0]);
            points.push({ mois: m, payeursCumules: payeurs, montantCumule: montant });
        }
        const borne = Math.min(12, Math.max(1, moisArrete));
        const aLaDate = points[borne - 1] ?? { payeursCumules: 0, montantCumule: 0 };
        const couverture = totalMontant > 0 ? montantDate / totalMontant : 0;
        return {
            exercice: annee,
            points,
            totalPayeurs: vuesTotal.size,
            totalMontant,
            couvertureDates: couverture,
            exploitable: couverture >= COUVERTURE_MINIMALE_DATES,
            aLaDate: { payeurs: aLaDate.payeursCumules, montant: aLaDate.montantCumule },
            enCours: annee === exerciceEnCours,
        };
    });
}
/**
 * Avancement de l'exercice en cours.
 *
 *     payeurs projetés      = assiette d'appel × taux de recouvrement constaté
 *     cotisations projetées = payeurs projetés × produit moyen constaté
 *     appels manquants      = cible ÷ (produit moyen × taux) − assiette
 *
 * Le raisonnement porte sur l'**assiette d'appel**, non sur les encaissements :
 * c'est la grandeur qui commande le produit de l'exercice, et c'est aussi le
 * levier — une cotisation non appelée ne se recouvre pas.
 *
 * Le **rythme de facturation** est rendu à côté, sans entrer dans le verdict :
 * une facture tardive décale l'encaissement, elle ne réduit pas l'assiette. Les
 * deux se lisent ensemble — assiette conforme et facturation en retard décrit
 * un problème de calendrier, assiette insuffisante décrit un problème de fond.
 *
 * Le verdict tolère `tolerance` d'écart relatif avant de parler d'avance ou de
 * retard. Sans assiette ni taux exploitable, il reste indéterminé.
 */
export function avancementCotisations(e) {
    const tolerance = e.tolerance ?? 0.05;
    const payeursProjetes = Math.round(e.appels * e.tauxRecouvrement);
    const cotisationsProjetees = Math.round(payeursProjetes * e.produitMoyen * 100) / 100;
    const ecartCible = sommeExacte([cotisationsProjetees, -e.cible]);
    const rendement = e.produitMoyen * e.tauxRecouvrement;
    const appelsNecessaires = rendement > 0 ? Math.ceil(e.cible / rendement) : 0;
    let verdict = 'indetermine';
    if (e.cible > 0 && e.appels > 0 && e.tauxRecouvrement > 0 && e.produitMoyen > 0) {
        const relatif = (cotisationsProjetees - e.cible) / e.cible;
        verdict = relatif > tolerance ? 'en_avance' : relatif < -tolerance ? 'en_retard' : 'conforme';
    }
    return {
        exercice: e.exercice,
        dateArrete: e.dateArrete,
        appels: e.appels,
        facturesEmises: e.facturesEmises,
        facturesPrecedentes: e.facturesPrecedentes,
        ratioFacturation: e.facturesPrecedentes > 0 ? e.facturesEmises / e.facturesPrecedentes : null,
        acquitte: e.acquitte,
        payeurs: e.payeurs,
        tauxRecouvrement: e.tauxRecouvrement,
        produitMoyen: e.produitMoyen,
        payeursProjetes,
        cotisationsProjetees,
        cible: e.cible,
        ecartCible,
        verdict,
        appelsManquants: Math.max(0, appelsNecessaires - e.appels),
    };
}
/** Libellé français d'un verdict d'avancement. */
export function libelleVerdict(v) {
    switch (v) {
        case 'en_avance':
            return 'En avance sur la cible';
        case 'conforme':
            return 'Conforme à la cible';
        case 'en_retard':
            return 'En retard sur la cible';
        case 'indetermine':
            return 'Indéterminé';
    }
}
//# sourceMappingURL=rythme.js.map